import { type EditAction } from '@/application';
import { type Evaluated } from '@/domain/construction';
import { type Vec2 } from '@/domain/math';
import { type EntityId, type SceneDocument, type Style } from '@/domain/model';
import { type DragAnchor, type Scene } from '@/engine';
import { type TransformBox } from '@/rendering';

import { type SnapResult, type SnapSettings } from '../Snapping';

/**
 * The tool contract.
 *
 * Every tool is a **finite state machine** with its states named explicitly, not a set of
 * mutable flags. In an editor carrying thirty-seven tools, "am I mid-drag or
 * mid-rubber-band?" answered by three booleans is how tools start behaving differently
 * depending on the order events happened to arrive.
 *
 * Tools never mutate anything. They return `EditAction`s and let the application layer
 * apply them, which is what makes every gesture undoable in M8 without revisiting a
 * single tool.
 */

export interface Modifiers {
  readonly shift: boolean;
  readonly alt: boolean;
  /** Ctrl on Windows and Linux, ⌘ on macOS — normalised by the router. */
  readonly primary: boolean;
}

export const NO_MODIFIERS: Modifiers = Object.freeze({
  shift: false,
  alt: false,
  primary: false,
});

export interface PointerInput {
  readonly world: Vec2;
  readonly screen: Vec2;
  readonly modifiers: Modifiers;
  readonly button: number;
}

export interface ToolContext {
  readonly document: SceneDocument;
  readonly scene: Scene;
  readonly selection: ReadonlySet<EntityId>;
  /** Screen pixels per world unit. */
  readonly zoom: number;
  /** Pick radius already converted to world units. */
  readonly tolerance: number;
  readonly snapSettings: SnapSettings;
  /** Allocates a unique entity id with the given prefix. */
  nextId(prefix: string): EntityId;
  /** Style for newly created entities. */
  readonly style: Style;
}

/**
 * Everything a tool wants to happen as a result of one event.
 *
 * All fields optional: most events produce only a preview, and a tool that returns
 * nothing has simply not reached a decision yet.
 */
/** What a tool is waiting for right now. */
export interface ToolPrompt {
  /** The tool's own name, so the strip says what is in hand. */
  readonly tool: string;
  /** Picks already made. */
  readonly step: number;
  /** Picks the tool needs in total. */
  readonly steps: number;
  /** What to click next. */
  readonly hint: string;
  /**
   * Why the thing under the cursor cannot be used.
   *
   * A refusal the user cannot account for reads as a broken tool. The cursor already says
   * *no*; this says *why*, which is the difference between "try something else" and "give up".
   */
  readonly problem?: string;
}

export interface ToolResponse {
  readonly actions?: readonly EditAction[] | undefined;
  readonly selection?: readonly EntityId[] | undefined;
  /** Ghost geometry drawn on the overlay while the gesture is in progress. */
  readonly preview?: readonly Evaluated[] | undefined;
  /** Where the snap indicator should be drawn. */
  readonly snap?: SnapResult | undefined;
  /** Pins a dragged point towards the pointer while constraints are solved. */
  readonly anchor?: DragAnchor | undefined;
  readonly cursor?: string | undefined;
  /** Return to the selection tool — a one-shot tool signalling it is finished. */
  readonly finished?: boolean | undefined;

  /** Shown in the undo menu. Defaults to the tool's label. */
  readonly label?: string | undefined;
  /**
   * Gesture identity for history compression.
   *
   * Consecutive edits sharing a key become one undo step. A drag sets it so that a
   * four-hundred-event gesture costs one press of undo rather than four hundred.
   */
  readonly mergeKey?: string | undefined;
  /** Ends the current gesture, so the next edit starts a fresh undo step. */
  readonly sealHistory?: boolean | undefined;
}

export interface Tool {
  readonly id: string;
  readonly label: string;
  /** Keyboard shortcut, matched exactly against `event.key`. Case is significant. */
  readonly shortcut?: string;

  /**
   * Toolbar section this tool belongs to.
   *
   * Grouping became necessary rather than decorative at M11: a flat list of thirty-seven
   * tools is taller than the viewport and unsearchable by eye. The tool declares its own
   * section so the toolbar stays a renderer of what it is handed.
   */
  readonly group?: string;

  /**
   * Whether this tool changes the *structure* of the drawing. Defaults to true.
   *
   * Explore mode exists so a construction can be handed to a class that may examine it and not
   * dismantle it. What that forbids is adding and removing — and `blocksInExploreMode` already
   * says exactly that, letting `update-entity` through as a change of appearance.
   *
   * The tool list needs the same rule, and used to hard-code it as "the selection tool and
   * nothing else". That was the right answer while selection was the only tool whose edits were
   * all cosmetic; it is the wrong shape, because it locks the decision to an identity rather
   * than to a property. A tool that only rewrites coordinates says so here, and the guarantee
   * still rests where the doc comment for explore mode says it does — on the dispatch.
   */
  readonly restructures?: boolean;

  /**
   * What the tool is waiting for, for the shell to show.
   *
   * A tool that takes several picks is unusable without this. The strings existed from the
   * start and were never displayed anywhere, which left a three-click tool giving no sign
   * that the first two clicks had registered — the user could only get it right by already
   * knowing the sequence.
   */
  prompt?(): ToolPrompt | null;

  onPointerDown?(context: ToolContext, input: PointerInput): ToolResponse | null;
  onPointerMove?(context: ToolContext, input: PointerInput): ToolResponse | null;
  onPointerUp?(context: ToolContext, input: PointerInput): ToolResponse | null;
  /** Return a response to consume the key, `null` to let it fall through. */
  onKeyDown?(context: ToolContext, key: string, modifiers: Modifiers): ToolResponse | null;

  /**
   * The transform frame to draw around the selection, if this tool offers one.
   *
   * **Pulled, not pushed**, and that is the point. A frame delivered through `ToolResponse`
   * would exist only after a pointer event: pressing the tool's shortcut with something already
   * selected would show nothing until the mouse was wiggled, and clicking a row in the object
   * list would leave the frame on the previous selection. Worse, `dispatch` emits only when
   * something changed, so a pointer move that produced no edit — a turn that rounded to the
   * angle it was already at — would freeze the frame while the pointer kept going.
   *
   * Asking the tool instead means the frame is always a function of the current state, which is
   * what it should have been in the first place.
   */
  overlayBox?(context: ToolContext): TransformBox | null;

  /**
   * Abandons any partial gesture.
   *
   * Every tool must be cancellable at any state — the README calls this out because a
   * tool that traps the user mid-construction with no way back is worse than one that
   * does nothing at all.
   */
  cancel(): ToolResponse | null;

  /** Discards state when the tool is deactivated. */
  reset(): void;
}

/** True when the pointer moved far enough to count as a drag rather than a click. */
export function exceedsDragThreshold(from: Vec2, to: Vec2, zoom: number): boolean {
  const DRAG_THRESHOLD_PIXELS = 3;
  const dx = (to.x - from.x) * zoom;
  const dy = (to.y - from.y) * zoom;
  return Math.hypot(dx, dy) >= DRAG_THRESHOLD_PIXELS;
}
